/* xlsx.js (C) 2013-present  SheetJS -- http://sheetjs.com */
/* Notes:
   - usage: `ReactDOM.render( <SheetJSApp />, document.getElementById('app') );`
   - xlsx.full.min.js is loaded in the head of the HTML page
   - this script should be referenced with type="text/babel"
   - babel.js in-browser transpiler should be loaded before this script
*/
import React, { memo } from "react"
import XLSX from "xlsx"
import { makeStyles } from "@material-ui/core/styles"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
import Box from "@material-ui/core/Box"

const useStyles = makeStyles(() => ({
    dataInputWrapper: {
        padding: "7px",
        margin: "5px 0",
        border: "1px solid #a5a5a5",
        color: "#000",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "5px",
    },
    formInputWrapper: {
        "&>input": {
            display: "none",
        },
    },
}))

const SheetJSAppHook = memo((props) => {
    const classes = useStyles()

    const { handleData } = props

    const handleFile = (file /*: File */) => {
        const reader = new FileReader()
        const rABS = !!reader.readAsBinaryString
        reader.onload = (e) => {
            /* Parse data */
            const bstr = e.target.result
            const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" })
            /* Get first worksheet */
            const wsname = wb.SheetNames[0]
            const ws = wb.Sheets[wsname]
            /* Convert array of arrays */
            const dataResult = XLSX.utils.sheet_to_json(ws, { header: 1 })
            /* Update state */
            handleData({ data: dataResult, cols: make_cols(ws["!ref"]) })
        }
        if (rABS) reader.readAsBinaryString(file)
        else reader.readAsArrayBuffer(file)
    }
    return (
        <Box>
            <Box className="upload-list">
                <DragDropFile handleFile={handleFile}>
                    <Box className={classes.dataInputWrapper}>
                        <Typography component="span">Drag and Drop Your Files Here</Typography>
                        <DataInput handleFile={handleFile} />
                    </Box>
                </DragDropFile>
            </Box>
        </Box>
    )
})

export default SheetJSAppHook

/* -------------------------------------------------------------------------- */

/*
  Simple HTML5 file drag-and-drop wrapper
  usage: <DragDropFile handleFile={handleFile}>...</DragDropFile>
    handleFile(file:File):void;
*/
const DragDropFile = (props) => {
    const { children } = props
    const suppress = (evt) => {
        evt.stopPropagation()
        evt.preventDefault()
    }
    const onDrop = (evt) => {
        evt.stopPropagation()
        evt.preventDefault()
        const { files } = evt.dataTransfer
        if (files && files[0]) props.handleFile(files[0])
    }
    return (
        <Box onDrop={onDrop} onDragEnter={suppress} onDragOver={suppress}>
            {children}
        </Box>
    )
}

/*
  Simple HTML5 file input wrapper
  usage: <DataInput handleFile={callback} />
    handleFile(file:File):void;
*/
const DataInput = (props) => {
    const classes = useStyles()
    const handleChange = (e) => {
        const { files } = e.target
        if (files && files[0]) props.handleFile(files[0])
    }
    return (
        <form className="form-inline form-position">
            <Box className={classes.formInputWrapper}>
                <label className="input-file-label" htmlFor="file_excel">
                    <Button variant="contained" color="primary" component="span" style={{ borderRadius: 20 }}>
                        Choose a File
                    </Button>
                </label>
                <input type="file" id="file_excel" name="file_excel" accept={SheetJSFT} onChange={handleChange} />
            </Box>
        </form>
    )
}

/* list of supported file types */
const SheetJSFT = ["xlsx", "csv"]
    .map(function (x) {
        return `.${x}`
    })
    .join(",")

/* generate an array of column objects */
// eslint-disable-next-line camelcase
const make_cols = (refstr) => {
    const o = []
    const C = XLSX.utils.decode_range(refstr).e.c + 1
    for (let i = 0; i < C; ++i) o[i] = { name: XLSX.utils.encode_col(i), key: i }
    return o
}
